// frontend/modules/in-app-notifications/components/NotificationDrawer.tsx
import React from 'react';
import { Drawer, Typography, Button, Space, Divider } from 'antd';
import { SettingOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useInAppNotifications } from '../hooks/useInAppNotifications';
import { NotificationList } from './NotificationList';
import { useTheme } from '@/components/common/ThemeProvider';

const { Title } = Typography;

interface NotificationDrawerProps {
    visible: boolean;
    onClose: () => void;
}

export const NotificationDrawer: React.FC<NotificationDrawerProps> = ({ 
    visible, 
    onClose 
}) => {
    const navigate = useNavigate();
    const { dark } = useTheme();
    const { 
        notifications, 
        stats, 
        isLoading, 
        markAllAsRead, 
        isMarkingAllAsRead 
    } = useInAppNotifications();

    const handleViewAll = () => {
        navigate('/notifications');
        onClose();
    };

    const handleMarkAllAsRead = async () => {
        try {
            await markAllAsRead();
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    // Show only first 10 notifications in drawer
    const drawerNotifications = notifications.slice(0, 10);

    return (
        <Drawer
            title={
                <div className="flex items-center justify-between">
                    <Title level={4} className="mb-0">
                        Notifications
                    </Title>
                    <Space>
                        {stats && stats.unreadCount > 0 && (
                            <Button
                                type="text"
                                size="small"
                                icon={<CheckOutlined />}
                                onClick={handleMarkAllAsRead}
                                loading={isMarkingAllAsRead}
                                className={dark ? 'text-white' : ''}
                            >
                                Mark all read
                            </Button>
                        )}
                        <Button
                            type="text"
                            size="small"
                            icon={<SettingOutlined />}
                            onClick={handleViewAll}
                            className={dark ? 'text-white' : ''}
                        >
                            View all
                        </Button>
                    </Space>
                </div>
            }
            placement="right"
            onClose={onClose}
            open={visible}
            width={400}
            className={dark ? 'dark-drawer' : ''}
        >
            <div className="notification-drawer-content">
                {stats && (
                    <div className={`mb-4 p-3 rounded ${dark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                        <Space split={<Divider type="vertical" />}>
                            <span>Total: {stats.totalCount}</span>
                            <span>Unread: {stats.unreadCount}</span>
                            <span>Attended: {stats.attendedCount}</span>
                        </Space>
                    </div>
                )}

                <NotificationList
                    notifications={drawerNotifications}
                    loading={isLoading}
                    showPagination={false}
                    onNotificationClick={(notification) => {
                        navigate(`/notifications/${notification.id}`);
                        onClose();
                    }}
                />

                {notifications.length > 10 && (
                    <div className="text-center mt-4">
                        <Button type="link" onClick={handleViewAll}>
                            View all {stats?.totalCount} notifications
                        </Button>
                    </div>
                )}
            </div>
        </Drawer>
    );
};
